import React, { useRef } from 'react';
import { styled } from '@compiled/react';
import { useFragment, graphql } from 'react-relay';
import Badge from '@atlaskit/badge';
import { Text } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import type { storyPointEstimate_issueFieldStoryPointEstimateReadviewFull_StoryPointEstimateReadView$key as StoryPointEstimateFragment } from '@atlassian/jira-relay/src/__generated__/storyPointEstimate_issueFieldStoryPointEstimateReadviewFull_StoryPointEstimateReadView.graphql';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import mergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import {
	STORY_POINT_ESTIMATE_BADGE_TEST_ID,
	STORY_POINT_ESTIMATE_TEST_ID,
} from '../../common/constants.tsx';
import type { StoryPointEstimateReadViewProps } from './types.tsx';
import messages from './messages.tsx';

export const StoryPointEstimateReadView = ({
	fragmentRef,
	shouldRenderHoverPopover,
}: StoryPointEstimateReadViewProps) => {
	const { formatNumber, formatMessage } = useIntl();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const data = useFragment<StoryPointEstimateFragment>(
		graphql`
			fragment storyPointEstimate_issueFieldStoryPointEstimateReadviewFull_StoryPointEstimateReadView on JiraNumberField {
				number
			}
		`,
		fragmentRef,
	);

	const { number } = data;
	const contentRef = useRef(null);

	if (number === null || number === undefined) {
		return (
			<span data-testid={STORY_POINT_ESTIMATE_TEST_ID}>
				<EmptyFieldText emptyText={isExperimentEnabled ? noValueTextExperiment : undefined} />
			</span>
		);
	}

	if (shouldRenderHoverPopover) {
		return (
			<HoverPopover contentRef={contentRef} content={formatNumber(number)}>
				{({ ref }) => (
					<span data-testid={STORY_POINT_ESTIMATE_TEST_ID}>
						<BadgeWrapper>
							<Badge testId={STORY_POINT_ESTIMATE_BADGE_TEST_ID}>
								<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
									{formatNumber(number)}
								</Text>
							</Badge>
						</BadgeWrapper>
					</span>
				)}
			</HoverPopover>
		);
	}

	return (
		<span data-testid={STORY_POINT_ESTIMATE_TEST_ID}>
			<BadgeWrapper>
				<Badge testId={STORY_POINT_ESTIMATE_BADGE_TEST_ID}>{formatNumber(number)}</Badge>
			</BadgeWrapper>
		</span>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const BadgeWrapper = styled.div({
	borderWidth: '2px',
	borderStyle: 'solid',
	borderColor: 'transparent',
});
